import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Clock, Download, AlertTriangle, RefreshCw, Users, Phone, Activity, Info } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtHms = (secs) => {
  if (!secs || secs <= 0) return "0:00";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  return `${h}:${String(m).padStart(2, "0")}`;
};

const fmtTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const fmtDate = (isoDate) => {
  if (!isoDate) return "";
  const [y, m, d] = isoDate.split("-");
  return `${d}/${m}`;
};

const PRESET_RANGES = [
  { label: "Today", days: 0 },
  { label: "Last 7 days", days: 6 },
  { label: "Last 30 days", days: 29 },
  { label: "This month", months: "current" },
];

const OfficeHoursTrackingPage = ({ Layout }) => {
  const today = new Date().toISOString().slice(0, 10);
  const sevenDaysAgo = new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10);
  const [start, setStart] = useState(sevenDaysAgo);
  const [end, setEnd] = useState(today);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/office/hours-tracking`, { params: { start, end } });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [start, end]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePreset = (preset) => {
    const today = new Date();
    if (preset.months === "current") {
      const first = new Date(today.getFullYear(), today.getMonth(), 1);
      setStart(first.toISOString().slice(0, 10));
      setEnd(today.toISOString().slice(0, 10));
    } else {
      const past = new Date(Date.now() - preset.days * 86400000);
      setStart(past.toISOString().slice(0, 10));
      setEnd(today.toISOString().slice(0, 10));
    }
  };

  const handleExport = async () => {
    try {
      const res = await axios.get(`${API}/office/hours-tracking/export`, {
        params: { start, end },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `office-hours-${start}-to-${end}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed: " + (err.response?.data?.detail || err.message));
    }
  };

  const content = (
    <div className="p-4 md:p-6 max-w-7xl mx-auto space-y-4" data-testid="office-hours-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
          <Clock className="h-6 w-6 text-[#2F5D8A]" />
          Office Hours Tracking
        </h1>
        <div className="flex gap-2">
          <button
            onClick={fetchData}
            disabled={loading}
            className="px-3 py-2 bg-gray-100 hover:bg-gray-200 text-sm rounded-lg flex items-center gap-1.5 disabled:opacity-50"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={handleExport}
            className="px-3 py-2 bg-[#2F5D8A] hover:bg-[#3B6D9C] text-white text-sm rounded-lg flex items-center gap-1.5"
            data-testid="export-csv-btn"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      {/* Date range */}
      <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-500 mb-1">Start</label>
          <input
            type="date"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            data-testid="start-date-input"
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 mb-1">End</label>
          <input
            type="date"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            data-testid="end-date-input"
          />
        </div>
        <div className="flex gap-1.5 ml-auto">
          {PRESET_RANGES.map((p) => (
            <button
              key={p.label}
              onClick={() => handlePreset(p)}
              className="px-2.5 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded"
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm" data-testid="error-msg">
          {error}
        </div>
      )}

      {loading && !data && (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      )}

      {data && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="summary-cards">
            {data.users.map((u) => (
              <div
                key={u.email}
                className="bg-white border border-gray-200 rounded-lg p-3"
                data-testid={`user-summary-${u.email}`}
              >
                <div className="text-xs text-gray-500 uppercase tracking-wide">{u.name}</div>
                <div className="mt-1 flex items-baseline justify-between">
                  <span className="text-lg font-bold text-gray-800">
                    {fmtHms(u.totals.logged_seconds)}
                  </span>
                  <span className="text-xs text-gray-500">
                    {u.totals.days_worked}d worked
                  </span>
                </div>

                {/* PRIMARY productivity metric: activity + rhythm */}
                <div className="mt-2 border-t border-gray-100 pt-2">
                  <div className="flex items-baseline justify-between">
                    <div className="text-xs text-gray-600 flex items-center gap-1">
                      <Activity className="h-3 w-3 text-[#2F5D8A]" />
                      <span className="font-semibold text-gray-800">{u.totals.activity_count}</span> events
                    </div>
                    {u.totals.rhythm_per_hour !== null && (
                      <span
                        className={`text-xs font-semibold ${
                          u.totals.rhythm_per_hour >= 3
                            ? "text-emerald-700"
                            : u.totals.rhythm_per_hour >= 1
                            ? "text-gray-600"
                            : "text-amber-700"
                        }`}
                        title="Orders acted upon per logged hour"
                      >
                        {u.totals.rhythm_per_hour}/h
                      </span>
                    )}
                  </div>
                </div>

                {/* SECONDARY / informational: RC talk-time when extension exists */}
                {u.has_rc_extension ? (
                  <div className="mt-2 text-[11px] text-gray-500 flex items-start gap-1">
                    <Phone className="h-3 w-3 mt-0.5 text-gray-400" />
                    <div>
                      <span className="text-gray-700">{fmtHms(u.totals.call_seconds)}</span> RC calls
                      <span
                        className="ml-1 text-amber-600 cursor-help"
                        title="Shared phone line — this number may include calls answered by other operators. Do not use as sole productivity metric."
                      >
                        (shared line)
                      </span>
                    </div>
                  </div>
                ) : (
                  <div className="mt-2 text-[11px] text-gray-400 italic">
                    No individual RC extension
                  </div>
                )}
                {u.totals.open_sessions > 0 && (
                  <div className="mt-1 text-xs text-amber-700 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    {u.totals.open_sessions} day(s) w/ orphan session
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Shared RC line warning banner */}
          {data.rc_line_shared_note && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 flex items-start gap-2" data-testid="shared-line-warning">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <span className="font-semibold">Shared RC phone line:</span> Ana e Vitória não têm ramais individuais; as ligações de João/Gabriela podem incluir chamadas atendidas por colegas.
                A métrica principal de produtividade é <b>eventos em ordens</b> (colunas <b>Events</b> e <b>Rhythm</b>) — não use o tempo de ligação como comparativo entre os 4.
              </div>
            </div>
          )}

          {/* Detailed per-user tables */}
          {data.users.map((u) => (
            <div
              key={u.email}
              className="bg-white border border-gray-200 rounded-lg overflow-hidden"
              data-testid={`user-table-${u.email}`}
            >
              <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-gray-500" />
                  <span className="font-semibold text-gray-700">{u.name}</span>
                  <span className="text-xs text-gray-500">({u.email})</span>
                </div>
                <span className="text-xs text-gray-500">
                  Total: <b className="text-gray-800">{fmtHms(u.totals.logged_seconds)}</b> logged
                  {" • "}
                  <b className="text-[#2F5D8A]">{u.totals.activity_count}</b> events
                  {u.totals.rhythm_per_hour !== null && (
                    <> • <b className="text-gray-800">{u.totals.rhythm_per_hour}</b> ords/h</>
                  )}
                  {u.has_rc_extension && u.totals.call_seconds > 0 && (
                    <> • <span className="text-gray-500">{fmtHms(u.totals.call_seconds)} RC (shared)</span></>
                  )}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600 text-xs uppercase">
                    <tr>
                      <th className="text-left px-3 py-2">Date</th>
                      <th className="text-left px-3 py-2">Login</th>
                      <th className="text-left px-3 py-2">Logout</th>
                      <th className="text-right px-3 py-2">Logged</th>
                      <th className="text-right px-3 py-2">Events</th>
                      <th className="text-left px-3 py-2">Breakdown</th>
                      <th className="text-right px-3 py-2">Rhythm</th>
                      <th className="text-right px-3 py-2">RC (shared)</th>
                      <th className="text-left px-3 py-2">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {u.days
                      .filter((d) => d.logged_seconds > 0 || d.open_session || d.activity_count > 0)
                      .map((d) => (
                        <tr
                          key={d.date}
                          className={`border-t border-gray-100 ${
                            d.open_session ? "bg-amber-50" : ""
                          }`}
                          data-testid={`day-row-${u.email}-${d.date}`}
                        >
                          <td className="px-3 py-2 font-medium text-gray-700">
                            {fmtDate(d.date)}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {fmtTime(d.first_login)}
                          </td>
                          <td className="px-3 py-2 text-gray-600">
                            {d.open_session ? (
                              <span className="text-amber-700 font-semibold">missing</span>
                            ) : (
                              fmtTime(d.last_logout)
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-semibold text-gray-800">
                            {fmtHms(d.logged_seconds)}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-[#2F5D8A]">
                            {d.activity_count}
                          </td>
                          <td className="px-3 py-2">
                            {Object.keys(d.activity_by_bucket || {}).length === 0 ? (
                              <span className="text-xs text-gray-300">—</span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {Object.entries(d.activity_by_bucket).map(([bucket, n]) => (
                                  <span
                                    key={bucket}
                                    className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700 border border-gray-200"
                                    title={`${n} events in ${bucket}`}
                                  >
                                    {bucket}: <b>{n}</b>
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {d.rhythm_per_hour !== null ? (
                              <span
                                className={`font-semibold ${
                                  d.rhythm_per_hour >= 3
                                    ? "text-emerald-700"
                                    : d.rhythm_per_hour >= 1
                                    ? "text-gray-700"
                                    : "text-amber-700"
                                }`}
                              >
                                {d.rhythm_per_hour}/h
                              </span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right text-gray-500">
                            {u.has_rc_extension ? fmtHms(d.call_seconds) : "—"}
                          </td>
                          <td className="px-3 py-2">
                            {d.open_session ? (
                              <span className="inline-flex items-center gap-1 text-amber-700 text-xs">
                                <AlertTriangle className="h-3 w-3" />
                                Logout não registrado — revisar
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">OK</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    {u.days.filter((d) => d.logged_seconds > 0 || d.open_session || d.activity_count > 0).length === 0 && (
                      <tr>
                        <td colSpan={9} className="px-3 py-6 text-center text-gray-400 text-xs">
                          No activity in this period
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      <div className="text-[11px] text-gray-400 mt-6 border-t pt-3">
        Login/logout is auto-recorded from <code className="bg-gray-100 px-1 rounded">/auth/login</code> and{" "}
        <code className="bg-gray-100 px-1 rounded">/auth/logout</code>. Sessions without a matching logout are
        capped at 8h and flagged for manual review.
      </div>
    </div>
  );

  return Layout ? <Layout>{content}</Layout> : content;
};

export default OfficeHoursTrackingPage;
